import { type FC, Suspense, useRef } from 'react';
import classnames from 'classnames';
import { graphql, useFragment, useLazyLoadQuery } from 'react-relay';

import dibsCss from 'dibs-css';
import { VideoTile } from 'dibs-elements/exports/VideoTile.ts';
import { VideoPlayer } from 'dibs-video/exports/VideoPlayer.ts';
import { buildMuxUrl } from 'dibs-video/exports/helpers.ts';

import { useHomePageContext } from '../HomePageContext.tsx';

import styles from './HpSharedVideoComponentVideo.scss';

import { type HpSharedVideoComponentVideo_componentModule$key } from './__generated__/HpSharedVideoComponentVideo_componentModule.graphql.ts';
import { type HpSharedVideoComponentVideoQuery as HpSharedVideoComponentVideoQueryType } from './__generated__/HpSharedVideoComponentVideoQuery.graphql.ts';

type Props = {
    componentModule: HpSharedVideoComponentVideo_componentModule$key;
};

const componentModuleFragment = graphql`
    fragment HpSharedVideoComponentVideo_componentModule on VideoModule {
        videoId
        videoAutoplay
        isMuxVideo
    }
`;

const videoQuery = graphql`
    query HpSharedVideoComponentVideoQuery($videoId: String!, $isMuxVideo: Boolean!) {
        viewer {
            video(videoId: $videoId) @skip(if: $isMuxVideo) {
                desktopFile: fileByVideoSize(size: w960h540) {
                    link
                }
                mobileFile: fileByVideoSize(size: w426h240) {
                    link
                }
            }
        }
    }
`;

type VideoContentProps = {
    videoId: string;
    autoPlay: boolean;
    isMuxVideo: boolean;
};

const VideoContent: FC<VideoContentProps> = ({ videoId, autoPlay, isMuxVideo }) => {
    const { isMobile } = useHomePageContext();
    const playerContainerRef = useRef<HTMLVideoElement>(null);

    const data = useLazyLoadQuery<HpSharedVideoComponentVideoQueryType>(videoQuery, {
        videoId,
        isMuxVideo,
    });

    const videoUrl = isMobile
        ? data?.viewer?.video?.mobileFile?.link
        : data?.viewer?.video?.desktopFile?.link;

    if (!videoUrl && !isMuxVideo) {
        return null;
    }

    return (
        <div
            className={classnames(dibsCss.relative, styles.videoContainer, {
                [dibsCss.flex]: isMuxVideo && isMobile,
                [dibsCss.justifyCenter]: isMuxVideo && isMobile,
                [styles.minWFull]: isMuxVideo,
            })}
        >
            {isMuxVideo ? (
                <div className={classnames(dibsCss.absolute, dibsCss.inset0)}>
                    <VideoPlayer forceMute={false} ref={playerContainerRef}>
                        <VideoPlayer.Media
                            src={buildMuxUrl({ playbackId: videoId })}
                            playback={{ autoPlay, loop: false, muted: true }}
                            objectFit="cover"
                        />
                        <VideoPlayer.Controls />
                    </VideoPlayer>
                </div>
            ) : (
                <VideoTile
                    controls
                    sourceUrl={videoUrl || ''}
                    noSpinner
                    loop={false}
                    autoPlay={autoPlay}
                />
            )}
        </div>
    );
};

const HpSharedVideoComponentVideo: FC<Props> = ({ componentModule: componentModuleRef }) => {
    const componentModule = useFragment(componentModuleFragment, componentModuleRef);
    const { videoId, videoAutoplay, isMuxVideo } = componentModule || {};

    if (!videoId) {
        return null;
    }

    return (
        <Suspense fallback={null}>
            <VideoContent videoId={videoId} autoPlay={!!videoAutoplay} isMuxVideo={!!isMuxVideo} />
        </Suspense>
    );
};

export default HpSharedVideoComponentVideo;
